// ---------------------------------------------------------------------------
// Primary-application intake + profile extraction, as background tasks.
//
// The uploaded application (a long PDF) is stored in SQLite with its extracted
// text: the original bytes stay downloadable from the Student Profile screen,
// and the text feeds the Application Intake Agent, which distributes the
// content into every raw profile field. Both AI steps run through the
// background-task runner so the user can keep using the app and gets a
// notification when they finish.
// ---------------------------------------------------------------------------

import * as repo from '../db/repository';
import { createProvider, providerContext } from '../ai';
import { useAppStore } from '../store/useAppStore';
import { runBackgroundTask } from './backgroundTasks';
import { readDocumentText } from '../utils/documentImport';
import { readFileAsBytes } from '../utils/download';
import { newId } from '../utils/id';
import { filterResolvedDatRedFlags } from '../utils/dat';
import type { ProfileInputs } from '../types';
import { EMPTY_PROFILE_INPUTS } from '../types';

export const INTAKE_TASK_KEY = 'profile:intake';
export const PROFILE_EXTRACT_TASK_KEY = 'profile:extract';

/**
 * Store the uploaded primary application (original bytes + extracted text) on
 * the active profile, creating a bare profile first if none exists yet.
 * Returns the owning profile id.
 */
export async function savePrimaryApplicationFile(file: File): Promise<string> {
  const [bytes, text] = await Promise.all([readFileAsBytes(file), readDocumentText(file)]);
  let profile = repo.getActiveProfile();
  if (!profile) {
    profile = repo.upsertProfile({
      id: newId('prof'),
      name: 'My Profile',
      inputs: { ...EMPTY_PROFILE_INPUTS },
      structured: null,
      createdAt: '',
      updatedAt: '',
    });
    useAppStore.getState().refreshProfile();
  }
  repo.savePrimaryApplication(
    profile.id,
    file.name,
    file.type || 'application/octet-stream',
    bytes,
    text,
  );
  return profile.id;
}

/**
 * Background task: run the Application Intake Agent over the stored primary
 * application and populate the profile's raw input fields from it. AI-filled
 * values win where the agent found content; fields it returned empty keep
 * whatever the user already typed.
 */
export function startApplicationIntake(profileId: string, filename: string): boolean {
  return runBackgroundTask({
    key: INTAKE_TASK_KEY,
    label: `Populating profile fields from ${filename}`,
    run: async () => {
      const text = repo.getPrimaryApplicationText(profileId);
      if (!text.trim()) {
        throw new Error(
          'No text could be extracted from the application (is it a scanned image PDF?).',
        );
      }
      const { settings } = useAppStore.getState();
      const provider = createProvider(settings);
      const extracted = await provider.extractProfileInputs(text, providerContext(settings));

      // Re-read the profile at completion time — the user may have edited it
      // (or the screen unmounted) while the AI ran.
      const profile = repo.getProfileById(profileId);
      if (!profile) throw new Error('The profile no longer exists.');
      const merged: ProfileInputs = { ...EMPTY_PROFILE_INPUTS, ...profile.inputs };
      for (const key of Object.keys(EMPTY_PROFILE_INPUTS) as (keyof ProfileInputs)[]) {
        if (extracted[key]?.trim()) merged[key] = extracted[key];
      }
      repo.upsertProfile({ ...profile, inputs: merged });
      useAppStore.getState().refreshProfile();
      return `Profile fields populated from ${filename} — review them in Student Profile.`;
    },
  });
}

/**
 * Background task: run the Profile Agent over the raw inputs to produce the
 * structured interview profile.
 */
export function startStructuredProfileExtraction(profileId: string): boolean {
  return runBackgroundTask({
    key: PROFILE_EXTRACT_TASK_KEY,
    label: 'Generating the structured profile',
    run: async () => {
      const profile = repo.getProfileById(profileId);
      if (!profile) throw new Error('The profile no longer exists.');
      const { settings } = useAppStore.getState();
      const provider = createProvider(settings);
      const extracted = await provider.extractProfile(profile.inputs, providerContext(settings));
      const structured = {
        ...extracted,
        redFlags: filterResolvedDatRedFlags(extracted.redFlags, profile.inputs),
      };
      const fresh = repo.getProfileById(profileId) ?? profile;
      repo.upsertProfile({ ...fresh, structured });
      useAppStore.getState().refreshProfile();
      return 'Structured interview profile generated.';
    },
  });
}
